/**
 * Fehlende Koordinaten nachtragen — die Rechnung, ohne Karte.
 *
 * 386 der 580 Plätze haben keine Position. Sie fehlen damit auf der Weltkarte
 * und in der Umkreissuche. Von Hand einzutragen ist das nur zu schaffen, wenn
 * die Karte beim Öffnen schon fast richtig steht — dafür sorgt die Ankerwahl.
 */

import { SPEICHER } from './db.js';
import { entfernung, hatKoordinaten } from './geo.js';
import { speichere } from './saetze.js';

/**
 * Grobe Mittelpunkte je Land, geschlüsselt nach ISO-Code.
 *
 * Bewusst keine geografischen Landesmitten, sondern die Tauchgebiete: Bei
 * Ägypten das Rote Meer statt der Wüste, bei Mexiko die Karibikküste statt
 * Zentralmexiko. Wer taucht, sucht seinen Platz am Wasser.
 *
 * Das ist eine Konstante, kein Geocoding — CLAUDE.md schliesst nur den Abruf
 * bei einem Dienst zur Laufzeit aus.
 */
export const LAENDER = {
  MV: { lat: 3.2, lon: 73.2, zoom: 7 },     // Malediven
  ID: { lat: -2.5, lon: 118.0, zoom: 5 },   // Indonesien
  EG: { lat: 26.8, lon: 33.8, zoom: 7 },    // Rotes Meer
  PH: { lat: 11.5, lon: 122.0, zoom: 6 },   // Philippinen
  FJ: { lat: -17.7, lon: 178.0, zoom: 7 },  // Fiji
  PW: { lat: 7.5, lon: 134.5, zoom: 9 },    // Palau
  MX: { lat: 20.5, lon: -87.0, zoom: 8 },   // Yucatán, Karibikseite
  AU: { lat: -18.3, lon: 147.0, zoom: 6 },  // Great Barrier Reef
  EC: { lat: -0.7, lon: -90.3, zoom: 7 },   // Galapagos
  PT: { lat: 38.5, lon: -28.0, zoom: 6 },   // Azoren
  CR: { lat: 9.6, lon: -85.0, zoom: 7 },    // Pazifikküste
  MM: { lat: 12.5, lon: 98.0, zoom: 7 },    // Mergui-Archipel
  TH: { lat: 8.5, lon: 98.3, zoom: 7 },     // Andamanensee
  CH: { lat: 46.8, lon: 8.2, zoom: 8 },     // Schweiz
  IT: { lat: 41.5, lon: 12.5, zoom: 6 },    // Italien
  ZA: { lat: -30.3, lon: 30.8, zoom: 7 },   // Aliwal Shoal
  AT: { lat: 47.6, lon: 13.8, zoom: 8 },    // Salzkammergut
};

const ZOOM_REISE = 12;
const ZOOM_LAND = 8;
const ZOOM_WELT = 2;

/** Alle Plätze, die zu denselben Reisen gehören wie dieser. */
function reisenachbarn(platz, plaetze, tauchgaenge) {
  const meineReisen = new Set(tauchgaenge
    .filter((tg) => tg.site_id === platz.id && tg.trip_id)
    .map((tg) => tg.trip_id));
  if (meineReisen.size === 0) return [];

  const nachbarIds = new Set(tauchgaenge
    .filter((tg) => meineReisen.has(tg.trip_id) && tg.site_id !== platz.id)
    .map((tg) => tg.site_id));
  return plaetze.filter((p) => nachbarIds.has(p.id) && hatKoordinaten(p));
}

/**
 * Wohin die Karte springt — vom Genauen zum Groben.
 *
 * Die Reihenfolge ist der ganze Trick: Ein Platz derselben Reise liegt meist
 * wenige Kilometer entfernt, ein Platz im selben Land immerhin in derselben
 * Ecke der Welt. Erst danach die Landesmitte.
 */
export function waehleAnker(platz, plaetze, tauchgaenge) {
  const [reise] = reisenachbarn(platz, plaetze, tauchgaenge);
  if (reise) return { lat: reise.lat, lon: reise.lon, zoom: ZOOM_REISE, grund: 'reise' };

  const imLand = plaetze.find((p) => p.id !== platz.id && hatKoordinaten(p)
    && p.country_code && p.country_code === platz.country_code);
  if (imLand) return { lat: imLand.lat, lon: imLand.lon, zoom: ZOOM_LAND, grund: 'land' };

  const mitte = LAENDER[platz.country_code];
  if (mitte) return { ...mitte, grund: 'landesmitte' };

  return { lat: 10, lon: 20, zoom: ZOOM_WELT, grund: 'welt' };
}

/** Die nächstgelegenen verorteten Plätze — als Orientierung auf der Karte. */
export function nachbarn(anker, plaetze, grenze = 50) {
  return plaetze
    .filter(hatKoordinaten)
    .map((platz) => ({ platz, km: entfernung(anker, platz) }))
    .sort((a, b) => a.km - b.km)
    .slice(0, grenze)
    .map((eintrag) => eintrag.platz);
}

/** Fünf Nachkommastellen sind gut einen Meter — alles darüber ist Rauschen. */
const rund = (wert) => Math.round(Number(wert) * 1e5) / 1e5;

/**
 * Position setzen und als von Hand gesetzt kennzeichnen.
 *
 * `geocode_source` unterscheidet sonst nicht, woher eine Koordinate kommt: Ein
 * GPS-Punkt vom Tauchcomputer, ein Wert aus Diving Log und ein Klick auf die
 * Karte sähen gleich aus. Die Marke wandert über den Abzug in die Datenbank —
 * die Spalte gibt es dort seit Migration 004.
 */
export async function setzePosition(db, platz, lat, lon, optionen = {}) {
  const neu = { ...platz, lat: rund(lat), lon: rund(lon), geocode_source: 'manual' };
  await speichere(db, SPEICHER.sites, neu, optionen);
  return neu;
}
